import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import formatoPDFUrl from "../assets/formato-directorio.pdf";

// ============================================================================
// CONSTANTES DE LAYOUT AJUSTABLES
// (Modificar aquí para afinar coordenadas según el diseño real del PDF de fondo)
// ============================================================================
export const MARGEN_SUPERIOR = 150; // debajo del membrete / encabezado
export const MARGEN_INFERIOR = 60;  // antes del pie de página
export const ALTO_FILA = 26;        // separación vertical entre registros

// Posiciones horizontales en X (en puntos tipográficos, 72 pt = 1 pulgada)
export const COL_CARGO_X = 40;
export const COL_NOMBRE_X = 160;
export const COL_UNIDAD_X = 320;
export const COL_EXTENSION_X = 480;
export const COL_CORREO_X = 540;

// Tamaño y formato tipográfico
export const TAMANO_FUENTE = 9;

// Anchos máximos recomendados para cada columna antes de truncar (opcional/ajustable)
export const ANCHO_MAX_CARGO = COL_NOMBRE_X - COL_CARGO_X - 8;
export const ANCHO_MAX_NOMBRE = COL_UNIDAD_X - COL_NOMBRE_X - 8;
export const ANCHO_MAX_UNIDAD = COL_EXTENSION_X - COL_UNIDAD_X - 8;
export const ANCHO_MAX_EXTENSION = COL_CORREO_X - COL_EXTENSION_X - 4;
export const ANCHO_MAX_CORREO = 65; // Margen derecho hacia borde de página (~612)

/**
 * Sanitiza texto para que no cause errores de codificación con fuentes estándar WinAnsi.
 */
function sanitizeWinAnsi(text = "") {
  return String(text ?? "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[\u2022]/g, "*")
    .replace(/[^\x00-\xFF]/g, "");
}

/**
 * Trunca texto si excede el ancho máximo disponible en la columna.
 */
function fitText(text, maxWidth, font, fontSize) {
  if (!text) return "";
  const safeText = sanitizeWinAnsi(text);
  if (!maxWidth || maxWidth <= 0) return safeText;

  if (font.widthOfTextAtSize(safeText, fontSize) <= maxWidth) {
    return safeText;
  }

  let low = 0;
  let high = safeText.length;
  let truncated = safeText;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = safeText.substring(0, mid) + "...";
    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      truncated = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return truncated;
}

/**
 * Carga el PDF de fondo como ArrayBuffer buscando en las rutas de assets.
 */
async function cargarFormatoBytes() {
  const posiblesRutas = [
    formatoPDFUrl,
    `${process.env.PUBLIC_URL || ""}/assets/formato-directorio.pdf`,
    "/assets/formato-directorio.pdf"
  ];

  for (const ruta of posiblesRutas) {
    if (!ruta) continue;
    try {
      const res = await fetch(ruta);
      if (res.ok) {
        return await res.arrayBuffer();
      }
    } catch {
      // Continuar con siguiente alternativa
    }
  }

  throw new Error(
    "No fue posible cargar el formato de fondo del directorio (formato-directorio.pdf)."
  );
}

/**
 * Genera el documento PDF del Directorio General en el navegador utilizando
 * el archivo PDF de fondo como plantilla visual repetible en cada página.
 *
 * @param {Array<Object>} data Lista de registros del directorio { cargo, nombre, unidad, extension, correo, orden }
 * @returns {Promise<Blob>} Blob del PDF generado listo para descarga
 */
export async function generarDirectorioPDF(data = []) {
  // 1. Obtener y parsear el PDF plantilla
  const formatoBytes = await cargarFormatoBytes();
  const templatePdfDoc = await PDFDocument.load(formatoBytes);

  // Obtener dimensiones de la página de fondo (ej. Carta: 612 x 792)
  const templatePage = templatePdfDoc.getPage(0);
  const { width, height } = templatePage.getSize();

  // 2. Crear el documento final e incrustar la página de fondo como plantilla reutilizable
  const outDoc = await PDFDocument.create();
  const [embeddedBackground] = await outDoc.embedPdf(templatePdfDoc, [0]);

  // Cargar fuentes estándar
  const fontRegular = await outDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await outDoc.embedFont(StandardFonts.HelveticaBold);
  const textColor = rgb(0.12, 0.12, 0.12);

  // Ordenar los datos por 'orden' si existe la propiedad, o mantener el orden recibido
  const registros = Array.isArray(data) ? [...data] : [];
  const tieneOrden = registros.some(
    (item) => item && item.orden !== undefined && item.orden !== null && item.orden !== ""
  );
  if (tieneOrden) {
    registros.sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0));
  }

  let currentPage = null;
  let currentY = 0;

  // Función interna para crear una nueva página con el fondo institucional
  const crearNuevaPagina = () => {
    const page = outDoc.addPage([width, height]);
    page.drawPage(embeddedBackground, {
      x: 0,
      y: 0,
      width,
      height
    });
    return page;
  };

  // Si no hay datos, generar al menos una hoja con el fondo oficial
  if (registros.length === 0) {
    crearNuevaPagina();
  } else {
    for (let i = 0; i < registros.length; i++) {
      const item = registros[i] || {};

      // Si es la primera fila o ya no cabe otra fila antes del margen inferior, crear nueva página
      if (!currentPage || currentY - ALTO_FILA < MARGEN_INFERIOR) {
        currentPage = crearNuevaPagina();
        currentY = height - MARGEN_SUPERIOR;
      }

      // Preparar y ajustar texto para cada columna
      const txtCargo = fitText(item.cargo, ANCHO_MAX_CARGO, fontBold, TAMANO_FUENTE);
      const txtNombre = fitText(item.nombre, ANCHO_MAX_NOMBRE, fontRegular, TAMANO_FUENTE);
      const txtUnidad = fitText(item.unidad, ANCHO_MAX_UNIDAD, fontRegular, TAMANO_FUENTE);
      const txtExtension = fitText(item.extension, ANCHO_MAX_EXTENSION, fontBold, TAMANO_FUENTE);
      const txtCorreo = fitText(item.correo, ANCHO_MAX_CORREO, fontRegular, TAMANO_FUENTE);

      // Dibujar columnas alineadas
      if (txtCargo) {
        currentPage.drawText(txtCargo, {
          x: COL_CARGO_X,
          y: currentY,
          size: TAMANO_FUENTE,
          font: fontBold,
          color: textColor
        });
      }

      if (txtNombre) {
        currentPage.drawText(txtNombre, {
          x: COL_NOMBRE_X,
          y: currentY,
          size: TAMANO_FUENTE,
          font: fontRegular,
          color: textColor
        });
      }

      if (txtUnidad) {
        currentPage.drawText(txtUnidad, {
          x: COL_UNIDAD_X,
          y: currentY,
          size: TAMANO_FUENTE,
          font: fontRegular,
          color: textColor
        });
      }

      if (txtExtension) {
        currentPage.drawText(txtExtension, {
          x: COL_EXTENSION_X,
          y: currentY,
          size: TAMANO_FUENTE,
          font: fontBold,
          color: textColor
        });
      }

      if (txtCorreo) {
        currentPage.drawText(txtCorreo, {
          x: COL_CORREO_X,
          y: currentY,
          size: TAMANO_FUENTE,
          font: fontRegular,
          color: textColor
        });
      }

      currentY -= ALTO_FILA;
    }
  }

  // 3. Serializar a Uint8Array y devolver como Blob
  const pdfBytes = await outDoc.save();
  return new Blob([pdfBytes], { type: "application/pdf" });
}

export default generarDirectorioPDF;
